/**
 * sl-oll9.26: News Portal Fullscreen View
 *
 * Renders the News Portal doc in fullscreen modal.
 * Uses the provisioned News Portal doc (articles + categories databases)
 * via the same pattern as ELSTER/KB fullscreen views.
 *
 * Data flow: Frontend loads News Portal doc → renders BlockSuite databases
 */

import { CloseIcon, DatabaseTableViewIcon } from '@blocksuite/icons/rc';
import { useService } from '@toeverything/infra';
import { type FC, useCallback, useEffect, useState } from 'react';

import { workspaceApiFetch } from '../../cloud';
import { WorkspaceService } from '../../workspace';
import type { FullscreenViewProps } from '../services/fullscreen-registry';

// Minimal inline styles (consistent with other fullscreen views)
const containerStyle: React.CSSProperties = {
  position: 'fixed',
  inset: 0,
  zIndex: 1000,
  display: 'flex',
  flexDirection: 'column',
  background: 'var(--streamlain-background-primary-color)',
};

const headerStyle: React.CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  padding: '12px 20px',
  borderBottom: '1px solid var(--streamlain-border-color)',
  background: 'var(--streamlain-background-secondary-color)',
};

const titleStyle: React.CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  gap: 8,
  fontSize: 16,
  fontWeight: 600,
};

const closeButtonStyle: React.CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  width: 32,
  height: 32,
  borderRadius: 4,
  border: 'none',
  background: 'transparent',
  cursor: 'pointer',
  color: 'var(--streamlain-text-primary-color)',
};

const bodyStyle: React.CSSProperties = {
  flex: 1,
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  justifyContent: 'center',
  padding: 40,
  gap: 16,
};

const provisionButtonStyle: React.CSSProperties = {
  padding: '10px 24px',
  borderRadius: 8,
  border: 'none',
  background: 'var(--streamlain-primary-color)',
  color: 'var(--streamlain-white)',
  fontSize: 14,
  fontWeight: 600,
  cursor: 'pointer',
};

interface NewsPortalConfig {
  workspaceId: string;
  featureType: string;
  docId: string | null;
  enabled: boolean;
}

export const NewsPortalFullscreenView: FC<FullscreenViewProps> = ({
  onClose,
}) => {
  const workspaceId = useService(WorkspaceService).workspace.id;

  const [config, setConfig] = useState<NewsPortalConfig | null>(null);
  const [loading, setLoading] = useState(true);
  const [provisioning, setProvisioning] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Fetch News Portal config
  const fetchConfig = useCallback(async () => {
    try {
      setLoading(true);
      const res = await workspaceApiFetch(
        `/api/workspace/${workspaceId}/news-portal/config`,
        { credentials: 'include' }
      );
      if (res.ok) {
        const data = await res.json();
        setConfig(data);
      } else if (res.status === 404) {
        setConfig(null);
      }
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to load config');
    } finally {
      setLoading(false);
    }
  }, [workspaceId]);

  useEffect(() => {
    void fetchConfig();
  }, [fetchConfig]);

  // Provision News Portal
  const handleProvision = useCallback(async () => {
    try {
      setProvisioning(true);
      setError(null);
      const res = await workspaceApiFetch(
        `/api/workspace/${workspaceId}/news-portal/provision`,
        { method: 'POST', credentials: 'include' }
      );
      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        throw new Error(data.message || `Provisioning failed (${res.status})`);
      }
      await fetchConfig();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Provisioning failed');
    } finally {
      setProvisioning(false);
    }
  }, [workspaceId, fetchConfig]);

  return (
    <div style={containerStyle}>
      <div style={headerStyle}>
        <div style={titleStyle}>
          <DatabaseTableViewIcon style={{ width: 20, height: 20 }} />
          News Portal
        </div>
        <button style={closeButtonStyle} onClick={onClose} aria-label="Close">
          <CloseIcon />
        </button>
      </div>

      <div style={bodyStyle}>
        {loading && <div>Loading...</div>}

        {!loading && error && (
          <div style={{ color: 'var(--streamlain-error-color)' }}>{error}</div>
        )}

        {!loading && !error && !config?.docId && (
          <>
            <DatabaseTableViewIcon
              style={{ width: 48, height: 48, opacity: 0.3 }}
            />
            <div style={{ fontSize: 16, fontWeight: 600 }}>
              News Portal einrichten
            </div>
            <div style={{ opacity: 0.6, textAlign: 'center', maxWidth: 400 }}>
              Erstellt Artikel- und Kategorien-Datenbanken fuer Ihr News Portal.
            </div>
            <button
              style={provisionButtonStyle}
              onClick={() => void handleProvision()}
              disabled={provisioning}
            >
              {provisioning ? 'Wird eingerichtet...' : 'News Portal erstellen'}
            </button>
          </>
        )}

        {!loading && !error && config?.docId && (
          <>
            <DatabaseTableViewIcon
              style={{ width: 48, height: 48, opacity: 0.3 }}
            />
            <div style={{ fontSize: 16, fontWeight: 600 }}>
              News Portal aktiv
            </div>
            <div style={{ opacity: 0.6, textAlign: 'center', maxWidth: 400 }}>
              Ihr News Portal ist eingerichtet. Oeffnen Sie das News Portal
              Dokument im Workspace um Artikel zu verwalten.
            </div>
          </>
        )}
      </div>
    </div>
  );
};
